interface NominatimResult {
    lat: string;
    lon: string;
    display_name: string;
    type?: string;
    class?: string;
    importance?: number;
    addresstype?: string;
    address?: {
      country?: string;
      state?: string;
      county?: string;
      city?: string;
      town?: string;
      village?: string;
    };
    boundingbox?: [string, string, string, string];
  }
  
  export interface GeocodingResult {
    lat: number;
    lng: number;
    display_name: string;
    locationType: string;
    importance: number;
    boundingbox: [string, string, string, string];
  }
  
  /**
   * Gets coordinates from an address string using OpenStreetMap Nominatim API
   * @param address Address string to geocode
   * @returns Geocoding result with standardized coordinates
   */
  export const getCoordinatesFromAddress = async (address: string): Promise<GeocodingResult> => {
    try {
      // Strategy 1: Try direct Nominatim API first (works on desktop, may fail on mobile due to CORS)
      const response = await fetch(
        // accept-language fixed to English: Nominatim ranks by language, so a Spanish or Norwegian
        // browser searching "Norway" otherwise gets Norway, Iowa instead of the country
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&addressdetails=1&accept-language=en`,
        {
          method: 'GET',
          mode: 'cors', // Enable CORS
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'WildJourney/1.0', // Required by Nominatim API
          },
          // Add timeout and other options
          signal: AbortSignal.timeout(10000), // 10 second timeout
        }
      );
  
      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status} ${response.statusText}`);
      }
  
      const results: NominatimResult[] = await response.json();
      
      if (results.length === 0) {
        throw new Error('Location not found');
      }

      return processGeocodingResult(results[0]);

    } catch (directError) {
      
      // Strategy 2: Fallback to backend proxy (no CORS issues)
      try {
        const proxyResponse = await fetch(
          `/api/geocode?address=${encodeURIComponent(address)}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
          }
        );

        if (!proxyResponse.ok) {
          const errorData = await proxyResponse.json().catch(() => ({}));
          throw new Error(errorData.error || `Backend geocoding error: ${proxyResponse.status}`);
        }

        const result = await proxyResponse.json();
        return result; // Backend already returns the correct format
        
      } catch (proxyError) {
        console.error('[Geocoding] Both direct API and backend proxy failed:', proxyError);
        throw new Error('Geocoding service unavailable');
      }
    }
  };

  /**
   * Process Nominatim result into standardized format
   */
  function processGeocodingResult(result: NominatimResult): GeocodingResult {
    // Determine location type based on address structure (SMART LOGIC RESTORED)
    let locationType = 'unknown';
    
    if (result.address?.country && !result.address?.state && !result.address?.city) {
      locationType = 'country';
    } else if (result.address?.state && !result.address?.city) {
      locationType = 'state';
    } else if (result.address?.county && !result.address?.city) {
      locationType = 'county';
    } else if (result.address?.city) {
      locationType = 'city';
    } else if (result.address?.town) {
      locationType = 'town';
    } else if (result.address?.village) {
      locationType = 'village';
    } else if (result.type) {
      locationType = result.type;
    } else if (result.class) {
      locationType = result.class;
    } else if (result.addresstype) {
      locationType = result.addresstype;
    }
    
    const coordinates: GeocodingResult = {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      display_name: result.display_name,
      locationType,
      importance: result.importance || 0.5,
      boundingbox: result.boundingbox || ['0', '0', '0', '0']
    };
    
    // Validate coordinates
    const coordinatesValid = validateCoordinates(coordinates);
    
    if (!coordinatesValid) {
      console.error('Invalid coordinates returned from geocoding:', coordinates);
      if (Math.abs(coordinates.lat) > 90 && Math.abs(coordinates.lng) <= 90) {
        const temp = coordinates.lat;
        coordinates.lat = coordinates.lng;
        coordinates.lng = temp;
      }
    }
    
    return coordinates;
  }

/**
 * Coordinate format utilities to standardize coordinate handling across the application.
 * MapLibre GL expects coordinates in [longitude, latitude] format.
 */

// Different coordinate format types used in the application
export type LatLngObject = { lat: number; lng: number };
export type MapLibreCoordinates = [number, number]; // [longitude, latitude]
export type LatLngCoordinates = [number, number]; // [latitude, longitude]

/**
 * Converts from {lat, lng} object format to MapLibre [longitude, latitude] array format
 * @param coords Object with lat/lng properties
 * @returns MapLibre coordinates in [lng, lat] format
 */
export function toMapLibreCoordinates(coords: LatLngObject | LatLngCoordinates): MapLibreCoordinates {
  if (Array.isArray(coords)) {
    // If array is provided, we assume it's in [lat, lng] format and convert it to [lng, lat]
    return [coords[1], coords[0]];
  }
  // Object format conversion
  return [coords.lng, coords.lat];
}

/**
 * Converts coordinates from [longitude, latitude] to {lat, lng} object format
 * @param coords MapLibre coordinates [lng, lat]
 * @returns Coordinate object with lat/lng properties
 */
export function fromMapLibreCoordinates(coords: MapLibreCoordinates): LatLngObject {
  return {
    lat: coords[1],
    lng: coords[0]
  };
}

/**
 * Detects if coordinates are swapped (latitude in longitude position and vice versa)
 * and fixes them to be in correct [longitude, latitude] format
 * @param coords Array of coordinates that might be in wrong order
 * @returns Properly ordered coordinates in [longitude, latitude] format
 */
export function fixSwappedCoordinates(coords: [number, number]): MapLibreCoordinates {
  // If first value is likely latitude (between -90 and 90) and 
  // second value is likely longitude (between -180 and 180)
  if (Math.abs(coords[0]) <= 90 && Math.abs(coords[1]) <= 180) {
    // Check if they might be swapped
    if (Math.abs(coords[0]) <= 90 && Math.abs(coords[1]) > 90) {
      // They appear to be already in [lng, lat] format
      return coords;
    }
    // They might be in [lat, lng] format, so we swap them
    return [coords[1], coords[0]];
  }
  
  // If first value is outside latitude range but second isn't, they're probably swapped
  if (Math.abs(coords[0]) > 90 && Math.abs(coords[1]) <= 90) {
    return [coords[1], coords[0]];
  }
  
  // Return as is if we can't determine the format or they seem correct
  return coords;
}

/**
 * Validates if coordinates are within valid ranges
 * @param coordinates Coordinates to validate, either as array or object
 * @returns true if coordinates are valid
 */
export function validateCoordinates(coordinates: LatLngObject | [number, number]): boolean {
  let lat: number, lng: number;
  
  if (Array.isArray(coordinates)) {
    // For arrays, we don't know the format without context, so we check both possibilities
    // Check if it's likely in [lng, lat] format
    if (Math.abs(coordinates[1]) <= 90 && Math.abs(coordinates[0]) <= 180) {
      lng = coordinates[0];
      lat = coordinates[1];
    } else {
      // Assume it might be in [lat, lng] format
      lat = coordinates[0];
      lng = coordinates[1];
    }
  } else {
    // Object format: {lat, lng}
    lat = coordinates.lat;
    lng = coordinates.lng;
  }
  
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

/**
 * Ensure coordinates are in a standardized format regardless of input type
 * @param coordinates Input coordinates in any supported format
 * @returns Standardized [longitude, latitude] coordinates
 */
export function standardizeCoordinates(coordinates: LatLngObject | [number, number]): MapLibreCoordinates {
  if (Array.isArray(coordinates)) {
    // For arrays, we need to determine if they're in [lat, lng] or [lng, lat] format
    const [first, second] = coordinates;
    
    // If first value is latitude (between -90 and 90) and second is longitude (between -180 and 180)
    if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
      // They're in [lat, lng] format, convert to [lng, lat]
      return [second, first];
    }
    
    // If first value is longitude (between -180 and 180) and second is latitude (between -90 and 90)
    if (Math.abs(first) <= 180 && Math.abs(second) <= 90) {
      // They're already in [lng, lat] format
      return coordinates as MapLibreCoordinates;
    }
    
    // If we can't determine the format, assume [lng, lat] and validate
    if (Math.abs(first) <= 180 && Math.abs(second) <= 90) {
      return coordinates as MapLibreCoordinates;
    }
    
    // If coordinates are invalid, throw an error
    throw new Error(`Invalid coordinates: ${JSON.stringify(coordinates)}`);
  } else {
    // For objects, convert from {lat, lng} to [lng, lat]
    return [coordinates.lng, coordinates.lat];
  }
}

/**
 * Get a formatted string for debug logging of coordinates
 * @param coordinates Coordinates to format for debug output
 * @returns Formatted string showing coordinates
 */
export function getCoordinateDebugString(coordinates: LatLngObject | [number, number]): string {
  let lat: number, lng: number;
  
  if (Array.isArray(coordinates)) {
    // For MapLibre format: [longitude, latitude]
    if (coordinates.length === 2) {
      // Try to detect format
      if (Math.abs(coordinates[0]) > 90 && Math.abs(coordinates[1]) <= 90) {
        // Looks like [lng, lat]
        lng = coordinates[0];
        lat = coordinates[1];
      } else {
        // Might be [lat, lng]
        lat = coordinates[0];
        lng = coordinates[1];
      }
    } else {
      return `Invalid coordinates array: ${JSON.stringify(coordinates)}`;
    }
  } else {
    // Object format: {lat, lng}
    lat = coordinates.lat;
    lng = coordinates.lng;
  }
  
  return `[lng: ${lng.toFixed(6)}, lat: ${lat.toFixed(6)}]`;
}